/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Divider, Tab } from '@mui/material'
import { type SubmitHandler, useForm, FormProvider } from 'react-hook-form'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { useAppDispatch } from '../../../store'
import { actionLoading } from '../../../store/slices/studentsSlice/selectors'
import { updateStudentsApi, createStudentsApi, getStudentsApi } from '../../../store/slices/studentsSlice/apis'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { notification } from '../../../components/Notifications'
import { getDirtyValues } from '../../../utils/onlyUpdatedFields'
import ValidationAlert from '../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../utils/showServerFieldErrors'
import { READONLY_TOOLTIP } from '../../../consts'
import { LastUpdateMeta } from '../../../components/GenericModal/ChangeMetaInfo'
import { type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import { useAccess } from '../../../hooks/useAccess'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { DetailsTab } from './DetailsTab'
import { EnrollmentsTab } from './EnrollmentsTab'
import { ModalDefaultAlert } from '../../../components/ModalDefaultAlert'
import { useUserRole } from '../../../hooks/useUserRole'
import FormSwitch from '../../../components/FormElements/FormSwitch'

interface FormData {
  firstName: string
  lastName: string
  studentId?: string
  email: string
  password?: string
  attributes?: any[]
  aliases?: string[]
  isCognitoProvisioned?: boolean
}

interface CreateAndEditStudentProps {
  selectedRow?: any
  selectedRowWithActions?: any
  handleCloseModal?: () => void
  params: ApplyFiltersAndDispatchParams
  isTaskRunning?: boolean
  onTaskBlock?: () => void
}
const StudentFormSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email format').required('Email is required'),
  password: yup.string().test('password', 'Password must be at least 6 characters',
    (password) => {
      if (!password) {
        return true
      }
      if (password.length < 6) {
        return false
      }
      return true
    }
  )
})

const CreateAndEditStudent: React.FC<CreateAndEditStudentProps> = ({ selectedRow, selectedRowWithActions, handleCloseModal, params, isTaskRunning, onTaskBlock }) => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const { activeRole } = useUserRole()
  const dispatch = useAppDispatch()
  const loading = actionLoading()
  const isEditAction = selectedRow !== undefined
  const showCognitoToggle = activeRole?.roleType === 'GLOBAL'
  const [errors, setErrors] = useState<unknown>({})
  const { firstName, lastName, studentId, email } = selectedRow ?? {}
  const [currTab, setCurrTab] = useState('Details')
  const defaultValues = useMemo<FormData>(() => ({
    firstName,
    lastName,
    studentId,
    email,
    attributes: selectedRow?.attributes ?? [],
    aliases: selectedRow?.aliases ?? [],
    isCognitoProvisioned: selectedRow?.isCognitoProvisioned ?? false
  }), [firstName, lastName, studentId, email, selectedRow?.attributes, selectedRow?.aliases, selectedRow?.isCognitoProvisioned])
  const methods = useForm<FormData>({
    resolver: yupResolver(StudentFormSchema),
    defaultValues
  })
  const { handleSubmit, control, reset, getValues, formState: { isDirty, dirtyFields } } = methods

  useEffect(() => {
    if (selectedRow != null && isTaskRunning) {
      onTaskBlock?.()
      handleCloseModal?.()
    }
  }, [selectedRow])

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  const onSubmit: SubmitHandler<FormData> = async (values) => {
    const isEmailInAliases = values.aliases?.includes(values.email)
    if (isEmailInAliases) {
      setErrors('Cannot use the same email in the Email and Email Aliases fields.')
      return
    }
    try {
      const { password, ...userPayload } = getDirtyValues(dirtyFields, values)
      const cognitoPayload = showCognitoToggle ? { isCognitoProvisioned: values.isCognitoProvisioned ?? false } : {}
      const currentAttributes = getValues('attributes')
      const attributesPayload = Array.isArray(currentAttributes) ? currentAttributes : []
      const apiCall = isEditAction
        ? dispatch(updateStudentsApi({ urlId: selectedRow.userId, data: { ...userPayload, ...cognitoPayload, attributes: attributesPayload } }))
        : dispatch(createStudentsApi({ data: { ...userPayload, ...cognitoPayload, attributes: attributesPayload, password: password === '' ? undefined : password } }))
      const res = await apiCall.unwrap()
      void dispatch(getStudentsApi({ params }))
      handleCloseModal?.()
      notification(`Student ${isEditAction ? 'Updated' : 'Added'} Successfully!`, { variant: 'success', subTitle: res.name })
    } catch (error) {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    }
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <TabContext value={currTab}>
          <Box>
            <TabList onChange={(e, newValue) => { setCurrTab(newValue) }}>
              <Tab label="Details" value="Details" />
              <Tab label="Enrollments" value="Enrollments" />
              {showCognitoToggle && <Tab label="Advanced Settings" value="Advanced Settings" />}
            </TabList>
          </Box>
          <TabPanel value="Details">
            <DetailsTab isEditAction={isEditAction} selectedRow={selectedRow} control={control} readonly={readonly} />
          </TabPanel>
          <TabPanel value="Enrollments">
            {isEditAction && (
              <EnrollmentsTab userId={selectedRow?.userId} />
            )}
            {!isEditAction && (
              <ModalDefaultAlert message="You need to create the student before managing enrollments." />
            )}
          </TabPanel>
          {showCognitoToggle && (
            <TabPanel value="Advanced Settings">
              <FormSwitch
                name="isCognitoProvisioned"
                label="Login via Classranked"
                defaultValue={selectedRow?.isCognitoProvisioned ?? false}
              />
            </TabPanel>
          )}
        </TabContext>
        <Divider sx={{ my: '24px' }} />
        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            type: 'submit',
            loading,
            disabled: readonly || (isEditAction && !isDirty),
            tooltip: readonly ? READONLY_TOOLTIP : undefined
          }}
          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.modifiedByUsername} lastEditedAt={selectedRow?.modifiedOn} />
          )}
        />
      </form>
    </FormProvider>
  )
}

export default CreateAndEditStudent
